import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Rating,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Quiz as QuizIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Psychology as PsychologyIcon,
  EmojiEvents as TrophyIcon,
  Article as ArticleIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import backgroundTopImage from '../../assets/concurso-pexels-tirachard-kumtanom-112571-733856.jpg';

const ExamPrepLanding: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <DashboardIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Dashboard Inteligente',
      description: 'Acompanhe seu progresso com gráficos radar e heatmaps que mostram seus pontos fortes e fracos por disciplina.',
    },
    {
      icon: <QuizIcon sx={{ fontSize: 40, color: '#2e7d32' }} />,
      title: 'Simulados com IA',
      description: 'Simulados personalizados baseados em concursos anteriores com explicações detalhadas para cada questão.',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#ed6c02' }} />,
      title: 'Análise de Desempenho',
      description: 'Receba sugestões automáticas: "Recomendo revisar Matemática Básica" ou "Foque nos erros em Interpretação de Texto".',
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 40, color: '#9c27b0' }} />,
      title: 'Planos Personalizados',
      description: 'Escolha seu concurso e receba o melhor plano de estudos adaptado às suas necessidades.',
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      title: 'Níveis de Dificuldade',
      description: 'Selecione entre Fácil, Médio ou Difícil para evoluir gradualmente em seus estudos.',
    },
    {
      icon: <TrophyIcon sx={{ fontSize: 40, color: '#f57c00' }} />,
      title: 'Gamificação',
      description: 'Sistema de pontuação, conquistas e rankings para manter você motivado nos estudos.',
    },
    {
      icon: <ArticleIcon sx={{ fontSize: 40, color: '#1565c0' }} />,
      title: 'Redações e Atualidades',
      description: 'Sugestões de temas atuais e correção de redações para concursos que exigem prova discursiva.',
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 40, color: '#388e3c' }} />,
      title: 'Notificações Inteligentes',
      description: 'Lembretes personalizados e dicas diárias para manter sua rotina de estudos em dia.',
    },
  ];

  const testimonials = [
    {
      name: 'Maria Silva',
      role: 'Aprovada no TRT-SP',
      avatar: '/api/placeholder/60/60',
      rating: 5,
      comment: 'Os simulados com IA me ajudaram a identificar exatamente onde eu precisava melhorar. Consegui minha aprovação!',
    },
    {
      name: 'João Santos',
      role: 'Aprovado na Receita Federal',
      avatar: '/api/placeholder/60/60',
      rating: 5,
      comment: 'O dashboard me mostrou minha evolução dia a dia. A gamificação me manteve motivado durante todo o processo.',
    },
    {
      name: 'Ana Costa',
      role: 'Aprovada no TCU',
      avatar: '/api/placeholder/60/60',
      rating: 5,
      comment: 'As explicações detalhadas das questões e as sugestões de estudo fizeram toda a diferença na minha preparação.',
    },
  ];

  const stats = [
    { number: '50K+', label: 'Concurseiros Aprovados' },
    { number: '1M+', label: 'Questões Resolvidas' },
    { number: '200+', label: 'Concursos Disponíveis' },
    { number: '95%', label: 'Taxa de Satisfação' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          backgroundImage: `url(${backgroundTopImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.85) 0%, rgba(66, 165, 245, 0.75) 100%)',
            zIndex: 1,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid component={'div' as React.ElementType} item xs={12} md={8}>
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom 
                fontWeight="bold"
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                Preparação de Concursos
              </Typography>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  color: 'white',
                  opacity: 0.95,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                Sua aprovação começa aqui! Plataforma inteligente para concursos públicos brasileiros.
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  color: 'white',
                  opacity: 0.9,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  maxWidth: '600px',
                  lineHeight: 1.6,
                }}
              >
                Utilizamos Inteligência Artificial para criar simulados personalizados,
                analisar seu desempenho e acelerar sua aprovação nos principais concursos do Brasil.
              </Typography>
              <Box sx={{ mt: 5 }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: '#1976d2',
                    px: 5,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    mr: 3,
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(0,0,0,0.3)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Começar Agora
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 5,
                    py: 2,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    borderRadius: 3,
                    textTransform: 'none',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-2px)',
                      borderWidth: 2,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Ver Demo
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid component={'div' as React.ElementType} item xs={6} md={3} key={index}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h3" component="div" color="primary" fontWeight="bold">
                  {stat.number}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
          Recursos que Aceleram sua Aprovação
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Tecnologia de ponta para maximizar seus resultados nos concursos públicos
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid component={'div' as React.ElementType} item xs={12} md={6} lg={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How it Works Section */}
      <Box sx={{ bgcolor: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Como Funciona
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
            Metodologia comprovada em 4 passos simples
          </Typography>

          <Grid container spacing={4}>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>1</Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary="Escolha seu Concurso"
                    secondary="Selecione o concurso desejado e receba um plano de estudos personalizado"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>2</Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary="Faça Simulados Inteligentes"
                    secondary="Resolva questões geradas por IA baseadas em provas anteriores"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: '#ed6c02', mr: 2 }}>3</Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary="Analise seu Desempenho"
                    secondary="Veja gráficos detalhados e receba sugestões personalizadas"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: '#9c27b0', mr: 2 }}>4</Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary="Conquiste sua Aprovação"
                    secondary="Evolua constantemente até alcançar seu objetivo"
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Box
                component="img"
                src="https://blobwgtechprod.blob.core.windows.net/assets/inspiracao-para-dashboard-gerencial-modelo-3.png?sp=r&st=2025-09-30T23:04:23Z&se=2031-01-03T07:19:23Z&sv=2024-11-04&sr=b&sig=9BSHk6Z2xlOagNsdJBTMhqygwoKsPvB0zbdyOyqVBDw%3D"
                alt="Dashboard da plataforma"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom fontWeight="bold">
          Histórias de Sucesso
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Veja o que nossos aprovados têm a dizer
        </Typography>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid component={'div' as React.ElementType} item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%', p: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={testimonial.avatar}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                      <Rating value={testimonial.rating} readOnly size="small" />
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                    "{testimonial.comment}"
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
            Pronto para sua Aprovação?
          </Typography>
          <Typography variant="h6" paragraph sx={{ opacity: 0.9, mb: 4 }}>
            Junte-se a milhares de concurseiros que já conquistaram suas vagas com nossa plataforma.
          </Typography>
          <Chip
            label="🚀 Lançamento em breve"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontSize: '1rem',
              py: 2,
              px: 3,
              mb: 4,
            }}
          />
          <Box>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#2e7d32',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                mr: 2,
                '&:hover': {
                  bgcolor: '#f5f5f5',
                },
              }}
            >
              Cadastre-se para Early Access
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                navigate('/');
                window.scrollTo(0, 0);
              }}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Voltar ao Início
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#263238', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Preparação de Concursos
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Plataforma inteligente para aprovação em concursos públicos brasileiros.
                Tecnologia de ponta para acelerar seus estudos.
              </Typography>
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Contato
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Em breve: suporte@preparacaoconcursos.com.br
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4, pt: 4, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © {new Date().getFullYear()} Preparação de Concursos. Todos os direitos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ExamPrepLanding;